/**
 * Assigns or re-assigns page numbers to all models in the cache
 * @param data
 * @param currentPage
 * @return {*}
 */
function assignPageNumbers (data, currentPage) {
  for (let i in data) {
    data[i]['page'] = currentPage
  }
  return data
}

function updateVisiblePageResults (tempCache, currentPage) {
  let tempData = {}
  for (let i in tempCache) {
    if (tempCache[i].page === currentPage) {
      tempData[i] = Object.assign({}, tempCache[i])
    }
  }
  return tempData
}

/**
 * Returns the input DOM element type required for this field, based on the field type
 * @param type
 * @param hasChoices
 * @return {*}
 */
function mapInputTypeToFieldType (type, hasChoices) {
  switch (type) {
    case 'decimal':
      return 'money'
    case 'integer':
    case 'string':
      return 'input'
    case 'field':
    case 'choice':
      return hasChoices ? 'select' : 'autocomplete'
    case 'boolean':
      return 'checkbox'
    case 'date':
      return 'date'
  }
}

/**
 * Vuex mutation helper to receive data and assign a page number to each data point
 * Creates a clone of the cache, then adds new data to cache object and updates visible page data
 * @param state
 * @param results
 */
export function receiveData (state, results) {
  let { page, data, isNewModel, isUpdatedModel } = results
  if (!data || !Object.keys(data).length) return state.hasOwnProperty('data') ? state.data : state.cache
  if (isNewModel) {
    const amt = state.count > Object.keys(state.cache).length ? state.count : Object.keys(state.cache).length
    page = amt % 20 === 0 ? Math.ceil(amt / 20) + 1 : Math.ceil(amt / 20)
  } else if (isUpdatedModel) {
    const existingModel = Object.keys(data).length ? state.cache[Object.keys(data)[0]] : null
    page = existingModel && existingModel.page ? existingModel.page : state.currentPage
  } else {
    page = page || (state.currentPage || 1)
  }
  const dataWithPageNumbers = assignPageNumbers(data, page)
  let tempCache = Object.assign({}, state.cache, dataWithPageNumbers)
  state.cache = Object.assign({}, tempCache)
  if (state.hasOwnProperty('data')) {
    state.data = Object.assign({}, updateVisiblePageResults(tempCache, isNewModel ? state.currentPage : page))
  }
}

/**
 * Vuex mutation helper to delete a data point
 * @param state
 * @param payload
 */
export function deleteData (state, payload) {
  let tempCache = Object.assign({}, state.cache)
  for (let c in tempCache) {
    if (tempCache[c].id === payload.id) {
      delete tempCache[c]
      if (state.count) state.count = state.count - 1
    }
  }
  state.cache = Object.assign({}, tempCache)
  if (state.hasOwnProperty('data')) {
    state.data = Object.assign({}, updateVisiblePageResults(state.cache, state.currentPage))
  }
}

/**
 * Vuex mutation helper to increment the count
 * @param state
 * @param payload
 */
export function increment (state, payload) {
  if (state.count && state.count > 0) {
    state.count = state.count + payload
  } else {
    state.count = payload
  }
  if (state.shown && state.shown < 20) {
    state.shown = state.shown + payload
  } else if (!state.shown) {
    state.shown = payload
  }
}

/**
 * Vuex mutation helper to receive and set metadata
 * @param state
 * @param payload
 */
export function receiveMetadata (state, payload) {
  if (payload) {
    const { page, count, next, previous, result } = payload
    if (page) state.currentPage = page
    if (count) state.count = count
    if (result) state.shown = result
    if (next) state.next = next
    if (previous) state.previous = previous
  } else if (state.data) {
    state.count = Object.keys(state.data).length
  }
}

/**
 * Vuex mutation helper to reset state metadata
 * @param state
 */
export function resetMetadata (state) {
  if (state.hasOwnProperty('count')) state.count = null
  if (state.hasOwnProperty('next')) state.next = null
  if (state.hasOwnProperty('previous')) state.previous = null
  if (state.hasOwnProperty('shown')) state.shown = null
  if (state.hasOwnProperty('currentPage')) state.currentPage = 1
}

/**
 * Vuex mutation helper to clear local state for given entity
 * @param state
 * @param payload {Boolean}   Whether to clear cached results as well
 */
export function clearLocalState (state, payload) {
  if (state.hasOwnProperty('data')) state.data = Object.assign({})
  if (state.hasOwnProperty('saving')) state.saving = false
  if (state.hasOwnProperty('saveFailure')) state.saveFailure = null
  if (state.hasOwnProperty('receivingData')) state.receivingData = false
  if (state.hasOwnProperty('receiveDataFailure')) state.receiveDataFailure = false
  if (state.hasOwnProperty('updating')) state.updating = null
  if (state.hasOwnProperty('updateFailure')) state.updateFailure = null
  if (state.hasOwnProperty('deleting')) state.deleting = false
  if (state.hasOwnProperty('deleteFailure')) state.deleteFailure = null
  if (state.hasOwnProperty('quote')) state.quote = Object.assign({})
  if (state.hasOwnProperty('receivingQuote')) state.receivingQuote = false
  if (state.hasOwnProperty('receiveQuoteFailure')) state.receiveQuoteFailure = null
  if (state.hasOwnProperty('cache') && payload) state.cache = Object.assign({})
}

/**
 * Vuex mutation helper to clear state
 * @param state
 */
export function clearState (state) {
  if (state.hasOwnProperty('data')) state.data = Object.assign({})
  if (state.hasOwnProperty('cache')) state.cache = Object.assign({})
  if (state.hasOwnProperty('lines')) state.lines = null
  if (state.hasOwnProperty('all')) state.all = Object.assign({})
  if (state.hasOwnProperty('allLines')) state.allLines = []
  if (state.hasOwnProperty('count')) state.count = null
  if (state.hasOwnProperty('shown')) state.shown = null
  if (state.hasOwnProperty('next')) state.next = null
  if (state.hasOwnProperty('previous')) state.previous = null
  if (state.hasOwnProperty('lineCount')) state.lineCount = null
  if (state.hasOwnProperty('nextLineUrl')) state.nextLineUrl = null
  if (state.hasOwnProperty('prevLineUrl')) state.prevLineUrl = null
  if (state.hasOwnProperty('currentPage')) state.currentPage = 1
  if (state.hasOwnProperty('saving')) state.saving = false
  if (state.hasOwnProperty('savingLines')) state.savingLines = false
  if (state.hasOwnProperty('saveFailure')) state.saveFailure = null
  if (state.hasOwnProperty('receivingData')) state.receivingData = false
  if (state.hasOwnProperty('receivingLines')) state.receivingLines = null
  if (state.hasOwnProperty('receiveDataFailure')) state.receiveDataFailure = false
  if (state.hasOwnProperty('updating')) state.updating = null
  if (state.hasOwnProperty('updatingLines')) state.updatingLines = false
  if (state.hasOwnProperty('updateFailure')) state.updateFailure = null
  if (state.hasOwnProperty('deleting')) state.deleting = false
  if (state.hasOwnProperty('deleteFailure')) state.deleteFailure = null
  if (state.hasOwnProperty('startingCheckNumbers')) state.startingCheckNumbers = Object.assign({})
  if (state.hasOwnProperty('receivingCheckNumbers')) state.receivingCheckNumbers = false
  if (state.hasOwnProperty('receivingCheckNumbersFailure')) state.receivingCheckNumbersFailure = null
  if (state.hasOwnProperty('quote')) state.quote = Object.assign({})
  if (state.hasOwnProperty('receivingQuote')) state.receivingQuote = false
  if (state.hasOwnProperty('receiveQuoteFailure')) state.receiveQuoteFailure = null
  if (state.hasOwnProperty('allProductionsLoaded')) state.allProductionsLoaded = false
  if (state.hasOwnProperty('allCompaniesLoaded')) state.allCompaniesLoaded = false
  if (state.hasOwnProperty('fields')) state.fields = Object.assign({})
  if (state.hasOwnProperty('reportOptions')) state.reportOptions = Object.assign({})
  if (state.hasOwnProperty('receivingFieldsFailure')) state.receivingFieldsFailure = ''
  if (state.hasOwnProperty('lastUpdated')) state.lastUpdated = Object.assign({})
  if (state.hasOwnProperty('createModalVisible')) state.createModalVisible = Object.assign({})
  if (state.hasOwnProperty('saving')) state.saving = false
  if (state.hasOwnProperty('isEditingForm')) state.isEditingForm = false
  if (state.hasOwnProperty('confirmRouteLeave')) state.confirmRouteLeave = false
  if (state.hasOwnProperty('initDataLoading')) state.initDataLoading = false
  if (state.hasOwnProperty('initDataLoaded')) state.initDataLoaded = false
}

export function deleteLine (state, payload) {
  if (state && state.lines && state.lines[payload]) {
    let tmpLineState = Object.assign({}, state.lines)
    delete tmpLineState[payload]
    state.lines = Object.assign({}, tmpLineState)
  }
}

export function receiveLinesMetadata (state, payload) {
  if (payload) {
    const { next, previous } = payload
    state.lineCount = Object.keys(state.lines).length
    if (next) state.nextLineUrl = next
    if (previous) state.prevLineUrl = previous
  } else if (state.data) {
    state.lineCount = Object.keys(state.lines).length
  }
}

export function clearTrxLines (state, payload) {
  let tmpLineState = Object.assign({}, state.lines)
  for (let line in tmpLineState) {
    if (tmpLineState[line].transaction_id === payload) {
      delete tmpLineState[line]
    }
  }
  state.lines = Object.assign({}, tmpLineState)
}

export function createFields (state, payload) {
  const { endpoint, fields } = payload
  delete fields.id
  let freshFields = Object.assign({}, fields)
  for (let field in freshFields) {
    if (field === 'id') continue
    freshFields[field].name = field
    freshFields[field].fieldType = mapInputTypeToFieldType(freshFields[field].type, !!freshFields[field].choices)
    if (freshFields[field].fieldType === 'autocomplete') {
      freshFields[field].searchAsync = true
      freshFields[field].triggerOnFocus = false
      freshFields[field].msg = `Please select a ${field}`
    }
    if (freshFields[field].fieldType === 'select') {
      freshFields[field].filterable = true
    }
  }
  state.fields[endpoint] = freshFields
}



// WEBPACK FOOTER //
// ./src/store/modules/helpers/mutationHelpers.js